// src/apply/ApplyPage.tsx
import { useState } from 'react'
import { useParams } from 'react-router-dom'

type FormState = 'idle' | 'submitting' | 'success' | 'error'

/**
 * Page publique de candidature — accessible sans authentification.
 * Le widget Turnstile officiel est chargé via un <script> CDN et injecté
 * dans la div #turnstile-widget. En dev (VITE_TURNSTILE_SITE_KEY absent),
 * on envoie le token 'dev-bypass' et le backend bypasse la vérification.
 */
export function ApplyPage() {
  const { slug } = useParams<{ slug: string }>()
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [message, setMessage] = useState('')
  const [formState, setFormState] = useState<FormState>('idle')
  const [errorMsg, setErrorMsg] = useState<string | null>(null)

  const siteKey = (import.meta as unknown as { env: Record<string, string | undefined> }).env['VITE_TURNSTILE_SITE_KEY']

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setFormState('submitting')
    setErrorMsg(null)

    // Récupérer le token Turnstile via l'objet global (injecté par le script CDN)
    // Si siteKey absent (dev local), utiliser 'dev-bypass'
    let turnstileToken = 'dev-bypass'
    try {
      const widgetEl = document.getElementById('turnstile-widget')
      const response = widgetEl?.querySelector<HTMLInputElement>(
        '[name="cf-turnstile-response"]'
      )
      if (response?.value) {
        turnstileToken = response.value
      }
    } catch {
      // Ignore — fallback sur dev-bypass
    }

    try {
      const res = await fetch(`/api/apply/${slug ?? ''}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim() || null,
          message: message.trim() || null,
          turnstileToken,
        }),
      })

      if (!res.ok) {
        const data = (await res.json()) as { error?: { message?: string } }
        throw new Error(data.error?.message ?? 'Erreur lors de l\'envoi')
      }

      setFormState('success')
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : 'Erreur inattendue')
      setFormState('error')
    }
  }

  if (formState === 'success') {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div data-testid="apply-success">
            <div style={styles.successIcon}>✓</div>
            <h1 style={styles.title}>Candidature envoyée !</h1>
            <p style={styles.subtitle}>
              Merci pour votre intérêt. Nous reviendrons vers vous prochainement.
            </p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <h1 style={styles.title}>Candidature — Devenir créateur partenaire</h1>
        <p style={styles.subtitle}>
          Remplissez ce formulaire pour rejoindre notre réseau de créateurs.
        </p>

        <form onSubmit={(e) => { void handleSubmit(e) }} style={styles.form}>
          <div style={styles.field}>
            <label htmlFor="apply-name" style={styles.label}>
              Nom complet <span style={styles.required}>*</span>
            </label>
            <input
              id="apply-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              placeholder="Votre nom"
              style={styles.input}
            />
          </div>

          <div style={styles.field}>
            <label htmlFor="apply-email" style={styles.label}>
              Email
            </label>
            <input
              id="apply-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="vous@exemple.com"
              style={styles.input}
            />
          </div>

          <div style={styles.field}>
            <label htmlFor="apply-message" style={styles.label}>
              Message
            </label>
            <textarea
              id="apply-message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Présentez-vous, vos plateformes, votre audience…"
              rows={5}
              style={{ ...styles.input, resize: 'vertical' }}
            />
          </div>

          {/* Widget Turnstile — CDN officiel Cloudflare */}
          {siteKey ? (
            <div
              id="turnstile-widget"
              className="cf-turnstile"
              data-sitekey={siteKey}
              style={{ marginBottom: '1rem' }}
            />
          ) : (
            <p style={styles.devNotice}>
              [Dev] Turnstile désactivé — VITE_TURNSTILE_SITE_KEY non défini.
            </p>
          )}

          {formState === 'error' && errorMsg && (
            <p style={styles.error}>{errorMsg}</p>
          )}

          <button
            type="submit"
            disabled={formState === 'submitting' || !name.trim()}
            style={{
              ...styles.button,
              opacity: formState === 'submitting' || !name.trim() ? 0.6 : 1,
              cursor: formState === 'submitting' || !name.trim() ? 'not-allowed' : 'pointer',
            }}
          >
            {formState === 'submitting' ? 'Envoi en cours…' : 'Envoyer ma candidature'}
          </button>
        </form>
      </div>
    </div>
  )
}

const styles = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'var(--bg, #0f0f0f)',
    padding: '2rem 1rem',
  } satisfies React.CSSProperties,
  card: {
    background: 'var(--surface, #1a1a1a)',
    border: '1px solid var(--line, #2a2a2a)',
    borderRadius: '14px',
    padding: '2.5rem',
    width: '100%',
    maxWidth: '480px',
  } satisfies React.CSSProperties,
  successIcon: {
    fontSize: '2.5rem',
    color: '#4a9a5c',
    textAlign: 'center' as const,
    marginBottom: '1rem',
  } satisfies React.CSSProperties,
  title: {
    fontSize: '1.5rem',
    fontWeight: 700,
    color: 'var(--fg, #f0f0f0)',
    letterSpacing: '-0.025em',
    margin: '0 0 0.5rem',
    textAlign: 'center' as const,
  } satisfies React.CSSProperties,
  subtitle: {
    fontSize: '0.9375rem',
    color: 'var(--muted, #888)',
    textAlign: 'center' as const,
    margin: '0 0 2rem',
  } satisfies React.CSSProperties,
  form: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '1.125rem',
  } satisfies React.CSSProperties,
  field: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '0.375rem',
  } satisfies React.CSSProperties,
  label: {
    fontSize: '0.875rem',
    fontWeight: 600,
    color: 'var(--fg, #f0f0f0)',
  } satisfies React.CSSProperties,
  required: {
    color: 'var(--arancio, #d9531e)',
  } satisfies React.CSSProperties,
  input: {
    background: 'var(--elevated, #222)',
    border: '1px solid var(--line-strong, #333)',
    borderRadius: '8px',
    padding: '0.5625rem 0.875rem',
    color: 'var(--fg, #f0f0f0)',
    fontSize: '0.9375rem',
    outline: 'none',
    width: '100%',
    boxSizing: 'border-box' as const,
    fontFamily: 'inherit',
  } satisfies React.CSSProperties,
  button: {
    background: 'var(--arancio, #d9531e)',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    padding: '0.625rem 1.25rem',
    fontSize: '0.9375rem',
    fontWeight: 600,
    fontFamily: 'inherit',
    width: '100%',
  } satisfies React.CSSProperties,
  devNotice: {
    fontSize: '0.8125rem',
    color: 'var(--dim, #555)',
    fontStyle: 'italic',
    margin: 0,
  } satisfies React.CSSProperties,
  error: {
    fontSize: '0.875rem',
    color: 'var(--arancio, #d9531e)',
    margin: 0,
  } satisfies React.CSSProperties,
}
